import { PriorityQueue } from "@datastructures-js/priority-queue";

export interface Keyed {
	key: string;
}

/**
 * A fixed-capacity active window backed by a PriorityQueue for overflow.
 *
 * Items added when the window has room go directly to active (insertion-ordered,
 * sticky). Items added when full go to waiting (ordered by comparator).
 * Releasing an active item promotes the highest-priority waiting item.
 */
export class WindowedPriorityQueue<T extends Keyed> {
	private readonly activeItems: Array<T> = [];
	private readonly waitingItems: PriorityQueue<T>;

	public constructor(
		private readonly capacity: number,
		compare: (a: T, b: T) => number,
	) {
		this.waitingItems = new PriorityQueue<T>(compare);
	}

	public add(item: T): void {
		if (this.has(item.key)) {
			return;
		}

		if (this.activeItems.length < this.capacity) {
			this.activeItems.push(item);
		} else {
			this.waitingItems.enqueue(item);
		}
	}

	public release(key: string): T | undefined {
		const activeIndex = this.activeItems.findIndex((item) => item.key === key);

		if (activeIndex === -1) {
			this.waitingItems.remove((v) => v.key === key);
			return undefined;
		}

		this.activeItems.splice(activeIndex, 1);

		const promoted = this.waitingItems.dequeue() ?? undefined;

		if (promoted) {
			this.activeItems.push(promoted);
		}

		return promoted;
	}

	public has(key: string): boolean {
		return this.waitingItems.contains((v) => v.key === key) || this.isActive(key);
	}

	public isActive(key: string): boolean {
		return this.activeItems.some((item) => item.key === key);
	}

	public active(): ReadonlyArray<string> {
		return this.activeItems.map((item) => item.key);
	}

	public waiting(): ReadonlyArray<string> {
		return this.waitingItems.toArray().map((v) => v.key);
	}

	public clear(): void {
		this.activeItems.length = 0;
		this.waitingItems.clear();
	}
}
